// Klient rozmowy z botem: jedno wywołanie POST /api/chat (klucz urządzenia w nagłówku). Błędy zamieniamy na kody, z których ekran rozmowy robi czytelny komunikat po polsku.
import type { CoachContext } from './coach.ts';
import { isKnown } from './vocab.ts';
import type { Progress } from '../types.ts';

export interface ChatTurn {
  role: 'child' | 'bot';
  text: string;
}

export interface ChatReply {
  say: string;
  pl?: string;
  /** `model` = odpowiedź Claude; `fallback` = gotowa linijka z puli bota (model dwa razy złamał zasady). */
  source: 'model' | 'fallback';
  /** Dla linijki awaryjnej: id linijki z puli bota (można zagrać jej nagranie ElevenLabs). */
  banterId?: string;
  /** To pożegnanie — rozmowa się kończy. */
  last: boolean;
  /** Stała odpowiedź „powiedz dorosłemu” (dziecko mówi o krzywdzie, strachu, niebezpieczeństwie). */
  safety?: true;
  diag?: { model: string; tries: number; violations: string[]; usage?: { input: number; output: number } };
}

export interface ChatRequest {
  day: number;
  turns: readonly ChatTurn[];
  /** Słowa, które dziecko ostatnio myli (z powtórek) — bot może ich użyć w pytaniu. */
  weak?: readonly string[];
  /** Bot w zadaniu (SPEC §14.5): pytanie dziecka / reakcja po próbie / komentarz na koniec lekcji. Bez tego — zwykła rozmowa. */
  context?: CoachContext;
}

export interface ReplyOptions {
  /** Po ilu ms przerywamy czekanie na odpowiedź (reakcja w zadaniu nie może trzymać dziecka dłużej). Bez limitu, gdy brak. */
  timeoutMs?: number;
}

export type ChatErrorCode =
  | 'unpaired' // serwer nie zna tego urządzenia (HTTP 401)
  | 'limit' // dzienny limit rozmów (HTTP 429)
  | 'off' // rozmowa wyłączona na serwerze: brak sekretu albo bazy (HTTP 503) albo brak backendu (404/HTML)
  | 'network' // brak połączenia
  | 'server'; // inny błąd serwera lub Anthropic — `detail` ma opis

export class ChatError extends Error {
  code: ChatErrorCode;
  constructor(code: ChatErrorCode, detail: string) {
    super(detail);
    this.name = 'ChatError';
    this.code = code;
  }
}

export interface ChatClient {
  reply(req: ChatRequest, opts?: ReplyOptions): Promise<ChatReply>;
}

export function createChatClient(headers: () => Record<string, string>, fetchImpl: typeof fetch = (...a) => fetch(...a), url = './api/chat'): ChatClient {
  return {
    async reply(req, opts = {}) {
      let res: Response;
      const controller = new AbortController();
      const timer = opts.timeoutMs ? setTimeout(() => controller.abort(), opts.timeoutMs) : undefined;
      const timedOut = () => new ChatError('network', `Odpowiedź bota nie przyszła w ciągu ${Math.round((opts.timeoutMs ?? 0) / 1000)} s.`);
      try {
        res = await fetchImpl(url, {
          method: 'POST',
          cache: 'no-store',
          headers: { 'content-type': 'application/json', ...headers() },
          body: JSON.stringify({ day: req.day, turns: req.turns, ...(req.weak && req.weak.length > 0 ? { weak: req.weak } : {}), ...(req.context ? { context: req.context } : {}) }),
          signal: controller.signal,
        });
      } catch (e) {
        if (timer) clearTimeout(timer);
        throw controller.signal.aborted ? timedOut() : new ChatError('network', e instanceof Error ? e.message : String(e));
      }
      let body = null as (Partial<ChatReply> & { error?: unknown }) | null;
      try {
        body = (await res.json()) as Partial<ChatReply> & { error?: unknown };
      } catch {
        body = null; // np. strona HTML z hostingu statycznego (brak funkcji /api/chat) albo przerwane czekanie na treść
      } finally {
        if (timer) clearTimeout(timer);
      }
      if (controller.signal.aborted) throw timedOut();
      const why = typeof body?.error === 'string' ? body.error : '';
      if (res.status === 401) throw new ChatError('unpaired', 'Serwer nie rozpoznaje tego urządzenia (HTTP 401).');
      if (res.status === 429) throw new ChatError('limit', 'Dzienny limit rozmów dla tego urządzenia jest wyczerpany (HTTP 429).');
      if (res.status === 503) throw new ChatError('off', why || 'Rozmowa jest wyłączona na serwerze (HTTP 503).');
      if (!res.ok) throw new ChatError(res.status === 404 && body === null ? 'off' : 'server', `HTTP ${res.status}${why ? ` — ${why}` : ''}`);
      if (!body || typeof body.say !== 'string' || body.say.trim() === '') throw new ChatError('off', 'Odpowiedź /api/chat nie jest odpowiedzią bota (brak backendu?).');
      return {
        say: body.say,
        ...(typeof body.pl === 'string' && body.pl ? { pl: body.pl } : {}),
        source: body.source === 'fallback' ? 'fallback' : 'model',
        ...(typeof body.banterId === 'string' ? { banterId: body.banterId } : {}),
        last: body.last === true,
        ...(body.safety === true ? { safety: true as const } : {}),
        ...(body.diag ? { diag: body.diag } : {}),
      };
    },
  };
}

/** Komunikat po polsku dla dziecka i rodzica. */
export function chatErrorMessage(e: unknown): string {
  if (!(e instanceof ChatError)) return `Rozmowa nie działa: ${e instanceof Error ? e.message : String(e)}`;
  switch (e.code) {
    case 'unpaired':
      return 'Ten tablet nie jest jeszcze połączony z serwerem. Rodzic: Dla rodzica ▸ Serwer ▸ Sparuj to urządzenie.';
    case 'limit':
      return 'Na dziś wystarczy rozmów. Wróć jutro!';
    case 'off':
      return `Rozmowa jest teraz wyłączona. ${e.message}`;
    case 'network':
      return 'Brak połączenia z internetem. Sprawdź sieć i spróbuj jeszcze raz.';
    default:
      return `Rozmowa chwilowo nie działa. ${e.message}`;
  }
}

/** Słowa kursu, które dziecko ostatnio myliło (najwięcej błędów, potem najniższe pudełko) — najwyżej 5. */
export function weakWords(progress: Progress, limit = 5): string[] {
  return Object.values(progress.course?.items ?? {})
    .filter((i) => i.wrong > 0)
    .sort((a, b) => b.wrong - a.wrong || a.box - b.box || a.word.localeCompare(b.word))
    .slice(0, limit)
    .map((i) => i.word);
}

/** Które dzisiejsze słówka dziecko wypowiedziało w rozmowie (z prostymi formami liczby mnogiej). */
export function usedWordsOf(childTexts: readonly string[], words: readonly string[]): string[] {
  const spoken = childTexts.join(' ').toLowerCase().replace(/[^a-z'\s]/g, ' ').split(/\s+/).filter(Boolean);
  return words.filter((w) => spoken.some((t) => isKnown(t, new Set([w.toLowerCase()]))));
}
